import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@remix-run/react';
import {
  COMMUNITY_IMAGE_CLASS_NAME,
  CommunityContentRenderer,
  type CommunityContentRendererProps,
  type MentionLinkProps,
  type MentionType,
} from '@shared/dream-components';
import type { JSONContent } from '@tiptap/core';

import { COMMUNITY_ROUTES } from '~/constants/community';
import { useCommunityVideoPlayback } from '~/routes/community/_hooks/useCommunityVideoPlayback';
import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';
import { subscribeToVideoStatus } from '~/routes/community/_utils/subscribeToVideoStatus';
import { toPostMediaImage } from '~/routes/community/_utils/toPostMediaImage';

import { ZoomableImage } from '../ui/ZoomableImage';

interface TiptapRendererProps {
  tiptapState: JSONContent;
  className?: string;
  // The post the body belongs to. Supplied where a bound video should play;
  // omitted (comments, previews) so video nodes render the neutral placeholder.
  postId?: string;
  // Post bodies open their images in the lightbox. Comments and the blurred gated preview keep plain images.
  zoomableImages?: boolean;
}

export const TiptapRenderer: FC<TiptapRendererProps> = ({ tiptapState, className, postId, zoomableImages = false }) => {
  const { t } = useTranslation();
  const videoPlayback = useCommunityVideoPlayback(postId);
  const videoTranslations = useMemo(
    () => ({
      'video.status_processing': t('video.status_processing'),
      'video.unavailable': t('video.unavailable'),
      'video.placeholder': t('video.placeholder'),
    }),
    [t],
  );

  return (
    <CommunityContentRenderer
      tiptapState={tiptapState}
      className={className}
      imageSrc={communityImageSrc}
      videoPlayback={videoPlayback}
      videoStatus={postId ? subscribeToVideoStatus : undefined}
      videoTranslations={videoTranslations}
      renderImage={zoomableImages ? renderImage : undefined}
      renderMentionLink={renderMentionLink}
      mentionFallbackLabel={t('community.join.former_member')}
    />
  );
};

const renderImage: CommunityContentRendererProps['renderImage'] = ({ src, alt }) => (
  <ZoomableImage images={[toPostMediaImage(src, alt)]} className="block" imageClassName={COMMUNITY_IMAGE_CLASS_NAME} />
);

// @everyone and badges have no page to link to, so their chips render as text.
const MENTION_ROUTES: Partial<Record<MentionType, (id: string) => string>> = { member: COMMUNITY_ROUTES.member };

const renderMentionLink = ({ type, id, className, children }: MentionLinkProps) => {
  const route = MENTION_ROUTES[type];
  if (!route) return <span className={className}>{children}</span>;

  return (
    <Link to={route(id)} className={className}>
      {children}
    </Link>
  );
};
